// src/components/General/analytics.tsx

/**
 * Utility functions to send Google Analytics (GA4) events via dataLayer.
 * Usage: Call these functions on user interactions to track events.
 */

// Ensure window.dataLayer exists
declare global {
   interface Window {
      dataLayer: Record<string, any>[];
   }
}

/**
 * Pushes a custom event to the dataLayer for GA4.
 * @param eventName - The name of the event (e.g., 'hero_selected')
 * @param eventParams - Additional parameters to send with the event
 */
export function sendGAEvent(eventName: string, eventParams: Record<string, any> = {}) {
   if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
         event: eventName,
         ...eventParams,
      });
   }
}

/**
 * Example: Track when a hero is selected.
 * @param heroName - The name of the selected hero
 */
export function trackHeroSelected(heroName: string) {
   sendGAEvent("hero_selected", { hero_name: heroName });
}

/**
 * Example: Track when a playlist is played.
 * @param playlistId - The ID of the playlist
 */
export function trackPlaylistPlayed(playlistUrl: string) {
   sendGAEvent("playlist_played", { playlist_url: playlistUrl });
}

/**
 * Example: Track generic button clicks.
 * @param buttonName - The name or label of the button
 */
export function trackCreatePlaylist(hero: string) {
   sendGAEvent("playlist_created", { hero_name: hero });
}

/**
 * Example: Track generic button clicks.
 * @param buttonName - The name or label of the button
 */
export function addAllTracksClicked(hero: string) {
   sendGAEvent("click_add_all", { hero_name: hero });
}
/**
 * Example: Track generic button clicks.
 * @param buttonName - The name or label of the button
 */
export function addSelectedTracksClicked(hero: string) {
   sendGAEvent("click_add_selected", { hero_name: hero });
}

/**
 * Track when a user toggles between heroes in the selector.
 * @param fromHero - The name of the previously selected hero
 * @param toHero - The name of the newly selected hero
 */
export function trackHeroToggled(hero: string) {
   sendGAEvent("hero_toggled", { hero_name: hero });
}